import React, { useState } from 'react';
import { HelpCircle, X, Mail, Send, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';

const SUPPORT_EMAIL = 'admin@nextflip.pro';

export const SupportButton = ({ variant = 'floating' }) => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });

  // Pre-fill form with user data when dialog opens
  const handleOpen = () => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/support`, form, 
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success('Message sent! We\'ll get back to you soon.');
      setIsOpen(false);
      setForm({ name: user?.name || '', email: user?.email || '', subject: 'general', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try email instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const subjectOptions = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'bug', label: 'Report a Bug' },
    { value: 'feature', label: 'Feature Suggestion' },
    { value: 'account', label: 'Account Help' },
    { value: 'billing', label: 'Billing Question' }
  ];

  // Floating button style
  if (variant === 'floating') {
    return (
      <>
        <button
          onClick={handleOpen}
          className="fixed bottom-6 left-6 z-50 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
          data-testid="floating-support-btn"
          aria-label="Contact Support"
        >
          <HelpCircle className="h-6 w-6" />
          <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Need Help?
          </span>
        </button>
        <SupportDialog 
          isOpen={isOpen} 
          setIsOpen={setIsOpen}
          form={form}
          setForm={setForm}
          submitting={submitting}
          handleSubmit={handleSubmit}
          subjectOptions={subjectOptions}
        />
      </>
    );
  }

  // Link style for navbar/footer
  if (variant === 'link') {
    return (
      <>
        <button
          onClick={handleOpen}
          className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
          data-testid="footer-support-link"
        >
          <Mail className="h-4 w-4" />
          Contact Support
        </button>
        <SupportDialog 
          isOpen={isOpen} 
          setIsOpen={setIsOpen}
          form={form}
          setForm={setForm}
          submitting={submitting}
          handleSubmit={handleSubmit}
          subjectOptions={subjectOptions}
        />
      </>
    );
  }

  // Navbar button style
  if (variant === 'navbar') {
    return (
      <>
        <button
          onClick={handleOpen}
          className="text-slate-600 hover:text-blue-700 transition-colors flex items-center gap-1.5 text-sm"
          data-testid="navbar-support-btn"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="hidden lg:inline">Support</span>
        </button>
        <SupportDialog 
          isOpen={isOpen} 
          setIsOpen={setIsOpen}
          form={form}
          setForm={setForm}
          submitting={submitting}
          handleSubmit={handleSubmit}
          subjectOptions={subjectOptions}
        />
      </>
    );
  }

  return null;
};

const SupportDialog = ({ isOpen, setIsOpen, form, setForm, submitting, handleSubmit, subjectOptions }) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Contact Support
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="support-name">Name *</Label>
              <Input
                id="support-name"
                required
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="support-email">Email *</Label>
              <Input
                id="support-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your@email.com"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="support-subject">Subject</Label>
            <select
              id="support-subject"
              value={form.subject}
              onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {subjectOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="support-message">Message *</Label>
            <Textarea
              id="support-message"
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
              placeholder="How can we help you?"
              className="mt-1"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button 
              type="submit" 
              disabled={submitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-full"
            >
              {submitting ? 'Sending...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=NextFlip Support`}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-full text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Email
            </a>
          </div>

          <p className="text-xs text-center text-slate-500">
            Or email us directly at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportButton;
